import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormSwitch,
  CLoadingButton,
} from '@coreui/react-pro'

import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import RoundService from 'src/service/RoundService'
import ToastComponent from 'src/components/common/TaostComponent'

const EditForm = ({ roundId, visible, onClose, fetchUsers, seasonDates }) => {
  const [roundDetail, setRoundDetail] = useState()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [errorMessages, setErrorMessages] = useState([])
  const [loader, setLoader] = useState(false)

  // Native date inputs take "YYYY-MM-DD" bounds; the season window limits what can be picked.
  const seasonMin = seasonDates?.start ? moment(seasonDates.start).format('YYYY-MM-DD') : undefined
  const seasonMax = seasonDates?.end ? moment(seasonDates.end).format('YYYY-MM-DD') : undefined

  useEffect(() => {
    if (visible && roundId) {
      RoundService.getRoundDetail(roundId)
        .then((res) => {
          if (res.status === 200) {
            if (res.data.start_date !== '0000-00-00 00:00:00') {
              setStartDate(moment(res.data.start_date).format('YYYY-MM-DD'))
              setStartTime(moment(res.data.start_date).format('HH:mm'))
            }
            if (res.data.end_date !== '0000-00-00 00:00:00') {
              setEndDate(moment(res.data.end_date).format('YYYY-MM-DD'))
              setEndTime(moment(res.data.end_date).format('HH:mm'))
            }
            setRoundDetail(res.data)
          }
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
        })
    }
  }, [visible, roundId])

  const validationSchema = Yup.object().shape({
    round: Yup.string().required('Round is required'),
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date()
      .required('End date is required')
      .min(Yup.ref('start_date'), 'End date must be later than start date'),
    start_time: Yup.string().required('Start time is required'),
    end_time: Yup.string()
      .required('End time is required')
      .test('is-not-same', 'End time cannot be the same as start time', function (value) {
        const { start_time, start_date, end_date } = this.parent
        if (moment(start_date).isSame(end_date, 'day')) {
          return value !== start_time
        }
        return true
      })
      .test('is-later', 'End time must be later than start time', function (value) {
        const { start_time, start_date, end_date } = this.parent
        if (moment(start_date).isSame(end_date, 'day')) {
          return moment(value, 'HH:mm').isAfter(moment(start_time, 'HH:mm'))
        }
        return true
      }),
  })

  const formik = useFormik({
    initialValues: {
      round: roundDetail?.round,
      start_date: startDate,
      end_date: endDate,
      start_time: startTime,
      end_time: endTime,
      trade_restriction: roundDetail?.trade_restriction,
      is_free_trades: roundDetail?.is_free_trades,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      const payload = {
        round_id: roundId,
        round: data.round,
        start_date: data.start_date,
        end_date: data.end_date,
        start_time: data.start_time,
        end_time: data.end_time,
        trade_restriction: data.trade_restriction,
        is_free_trades: data.is_free_trades,
      }

      setLoader(true)
      RoundService.editRound(payload)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers()
            ToastComponent(res.message, 'success')
            onClose()
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
          setLoader(false)
        })
    },
  })

  const handleClose = () => {
    setErrorMessages([])
    onClose()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 480 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Edit Round {roundDetail?.round}</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Update the lockout window and trade settings.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <CFormLabel htmlFor="round">Round *</CFormLabel>
            <CFormInput
              className={
                'form-control' + (formik.errors.round && formik.touched.round ? ' is-invalid' : '')
              }
              id="round"
              placeholder="Round"
              defaultValue={roundDetail?.round}
              onChange={formik.handleChange}
              disabled
            />
            {formik.errors.round && formik.touched.round && (
              <CFormFeedback invalid>{formik.errors.round}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="start_date">Round Start Date *</CFormLabel>
            <CFormInput
              type="date"
              id="start_date"
              name="start_date"
              min={seasonMin}
              max={seasonMax}
              value={formik.values.start_date || ''}
              invalid={!!(formik.errors.start_date && formik.touched.start_date)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.errors.start_date && formik.touched.start_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.start_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="start_time">Round Start Time *</CFormLabel>
            <CFormInput
              type="time"
              id="start_time"
              name="start_time"
              value={formik.values.start_time || ''}
              invalid={!!(formik.errors.start_time && formik.touched.start_time)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.errors.start_time && formik.touched.start_time && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.start_time}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="end_date">Round End Date *</CFormLabel>
            <CFormInput
              type="date"
              id="end_date"
              name="end_date"
              min={formik.values.start_date || seasonMin}
              max={seasonMax}
              value={formik.values.end_date || ''}
              invalid={!!(formik.errors.end_date && formik.touched.end_date)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.errors.end_date && formik.touched.end_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.end_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="end_time">Round End Time *</CFormLabel>
            <CFormInput
              type="time"
              id="end_time"
              name="end_time"
              value={formik.values.end_time || ''}
              invalid={!!(formik.errors.end_time && formik.touched.end_time)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.errors.end_time && formik.touched.end_time && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.end_time}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={12}>
            <hr className="mt-1 mb-0" />
          </CCol>
          <CCol xs={6}>
            <CFormSwitch
              id="trade_restriction"
              label={`Restrict Trade — ${roundDetail?.trade_restriction ? 'On' : 'Off'}`}
              checked={!!formik.values.trade_restriction}
              onChange={(e) => {
                setRoundDetail((prev) => ({ ...prev, trade_restriction: e.target.checked }))
                formik.setFieldValue('trade_restriction', e.target.checked)
                formik.setTouched({ ...formik.touched, trade_restriction: true })
              }}
            />
          </CCol>
          <CCol xs={6}>
            <CFormSwitch
              id="is_free_trades"
              label={`Free Trade — ${roundDetail?.is_free_trades ? 'On' : 'Off'}`}
              checked={!!formik.values.is_free_trades}
              onChange={(e) => {
                setRoundDetail((prev) => ({ ...prev, is_free_trades: e.target.checked }))
                formik.setFieldValue('is_free_trades', e.target.checked)
                formik.setTouched({ ...formik.touched, is_free_trades: true })
              }}
            />
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <CLoadingButton color="primary" loading={loader} onClick={() => formik.handleSubmit()}>
          Save Changes
        </CLoadingButton>
      </div>
    </COffcanvas>
  )
}

export default EditForm
